<template>
  <div>
    <div class="d-flex align-items-center mb-2">
      <div class="btn-group btn-group-sm mr-2" role="group">
        <button type="button" class="btn" :class="view === 'matrix' ? 'btn-active' : 'btn-outline-secondary'" @click="view = 'matrix'">Matrix</button>
        <button type="button" class="btn" :class="view === 'tree' ? 'btn-active' : 'btn-outline-secondary'" @click="view = 'tree'">Tree</button>
      </div>
      <button type="button" class="btn btn-outline-secondary btn-sm" @click="$emit('refresh')" :disabled="loading">
        <span v-if="loading" class="spinner-border spinner-border-sm mr-1" role="status"></span>
        {{ loading ? 'Loading…' : '↻ Refresh' }}
      </button>
      <span v-if="hint" class="small text-muted ml-2">{{ hint }}</span>
    </div>

    <PageLoader v-if="loading && !cells.length" :accent-color="accentColor" message="Deriving addresses…" />
    <div v-else-if="view === 'matrix'" class="row">
      <div v-for="cell in cells" :key="cell.index" class="col-sm-4 col-md-3 mb-2">
        <div class="addr-matrix-cell" :class="{ active: cell.index === activeIndex }" @click="$emit('select', cell.index)">
          <div class="addr-matrix-idx">#{{ cell.index }}</div>
          <div class="addr-matrix-addr text-break">{{ shortAddr(cell.address) }}</div>
          <div class="addr-matrix-bal">
            <span v-if="cell.loading">…</span>
            <span v-else>{{ (cell.balance || 0).toFixed(decimals) }} {{ unit }}</span>
          </div>
        </div>
      </div>
      <div v-if="!cells.length" class="col-12 text-muted small">No cached addresses yet — connect your Ledger to populate this view.</div>
    </div>

    <AddressTree v-else :data="treeData" :accent-color="accentColor" :unit="unit" :decimals="decimals" @select="({ addressIndex }) => $emit('select', addressIndex)" />
  </div>
</template>

<script>
import AddressTree from './AddressTree.vue';
import PageLoader from './PageLoader.vue';

export default {
  name: 'AddressMatrix',
  components: { AddressTree, PageLoader },
  props: {
    cells: { type: Array, required: true }, // [{ index, address, balance, loading }]
    activeIndex: { type: [Number, String], default: null },
    accentColor: { type: String, default: '#6c757d' },
    unit: { type: String, default: '' },
    decimals: { type: Number, default: 6 },
    loading: { type: Boolean, default: false },
    hint: { type: String, default: '' },
    rootLabel: { type: String, default: 'Account' },
  },
  data() {
    return { view: 'matrix' };
  },
  computed: {
    treeData() {
      return {
        name: this.rootLabel,
        children: this.cells.map(c => ({
          name: `#${c.index}`,
          address: c.address,
          balance: c.balance,
          loading: c.loading,
          addressIndex: c.index,
          isActive: c.index === this.activeIndex,
        })),
      };
    },
  },
  methods: {
    shortAddr(addr) {
      if (!addr) return '';
      return addr.length > 18 ? `${addr.slice(0, 10)}…${addr.slice(-6)}` : addr;
    },
  },
};
</script>

<style scoped>
.btn-active { background: var(--surface-2, #171b28); border: 1px solid #888; font-weight: 700; }
.addr-matrix-cell {
  border: 1px solid var(--border, #242a3a);
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
  background: var(--surface, #111520);
  transition: all 0.15s;
}
.addr-matrix-cell:hover { border-color: #888; }
.addr-matrix-cell.active { border-color: #d9790f; box-shadow: 0 0 0 1px #d9790f inset; }
.addr-matrix-idx { font-size: 0.7rem; font-weight: 700; color: var(--text-muted, #52597a); }
.addr-matrix-addr { font-size: 0.72rem; font-family: monospace; }
.addr-matrix-bal { font-size: 0.78rem; font-weight: 600; margin-top: 2px; }
</style>
